/**
 * Scheduled Tasks API Service
 * 
 * This service handles all API calls related to handyman scheduled tasks.
 * 
 * BACKEND API DOCUMENTATION:
 * Base URL: process.env.REACT_APP_API_URL
 * Authentication: Bearer token in Authorization header
 * 
 * ENDPOINTS:
 * 1. GET /scheduled-tasks - List all scheduled tasks for handyman
 * 2. GET /scheduled-tasks/:taskId - Get task details
 * 3. POST /scheduled-tasks/accept - Accept a task offer
 * 4. POST /scheduled-tasks/counter-offer - Make a counter offer
 * 5. POST /scheduled-tasks/reject - Reject a task offer
 * 6. POST /job/confirm-payment-received - Handyman confirms payment
 * 7. POST /approve-completion - Customer approves work
 * 8. POST /confirm-completion - Customer confirms completion and rates
 * 
 * SOCKET EVENTS TO LISTEN:
 * - offer:approved - Customer approved your offer
 * - offer:rejected - Customer rejected your offer
 * - task:scheduled - New task scheduled
 * - task:updated - Task details updated
 * - task:cancelled - Task cancelled
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => {
    return localStorage.getItem("authToken");
};

/**
 * Get user ID from localStorage
 */
const getUserId = () => {
    const authUser = localStorage.getItem("authUser");
    return authUser ? JSON.parse(authUser)._id : null;
};

/**
 * ENDPOINT 1: GET /scheduled-tasks
 * 
 * Fetch all scheduled tasks for the logged-in handyman
 * 
 * REQUEST:
 * - Method: GET
 * - Headers: { Authorization: "Bearer <token>" }
 * - Query params: None (handyman ID derived from token)
 * 
 * RESPONSE SUCCESS (200):
 * {
 *   success: true,
 *   data: [
 *     {
 *       taskId: "task_123",
 *       serviceRequestId: "req_456",
 *       customer: {
 *         customerId: "cust_789",
 *         name: "John Doe",
 *         phone: "+92 300 1234567",
 *         avatar: "https://...",
 *         rating: 4.5
 *       },
 *       service: {
 *         title: "Plumbing Repair",
 *         category: "Plumbing",
 *         subcategory: "Leak Repair",
 *         description: "Fix kitchen sink leak",
 *         notes: "Bring necessary tools"
 *       },
 *       location: {
 *         address: "123 Main St, Lahore",
 *         fullAddress: "House #123, Street 5, Gulberg III, Lahore",
 *         coordinates: [74.3140, 31.4782] // [lng, lat]
 *       },
 *       pricing: {
 *         basePrice: 2500,
 *         finalPrice: 2500,
 *         currency: "PKR"
 *       },
 *       schedule: {
 *         date: "2024-11-14",
 *         time: "09:00 AM",
 *         estimatedDuration: "2-3 hours"
 *       },
 *       status: "scheduled", // scheduled, in_progress, completed, cancelled
 *       offer: {
 *         offerId: "offer_123",
 *         handymanPrice: 2500,
 *         status: "accepted", // pending, accepted, rejected, counter
 *         createdAt: "2024-11-13T10:00:00Z"
 *       },
 *       createdAt: "2024-11-13T10:00:00Z",
 *       updatedAt: "2024-11-13T10:00:00Z"
 *     }
 *   ]
 * }
 * 
 * RESPONSE ERROR (401):
 * {
 *   success: false,
 *   message: "Unauthorized"
 * }
 * 
 * RESPONSE EMPTY (200):
 * {
 *   success: true,
 *   data: []
 * }
 */
export const getScheduledTasks = async () => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error("Authentication token not found");
        }

        const response = await fetch(`${API_BASE_URL}/handyman/scheduled-tasks`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();
        console.log(`✅ Fetched scheduled tasks in service:`, data);

        if (!response.ok) {
            throw new Error(data.message || "Failed to fetch scheduled tasks");
        }

        return {
            success: true,
            tasks: data.tasks || [],
            availableTasks: data.availableTasks || [],
        };
    } catch (error) {
        console.error("Error fetching scheduled tasks:", error);
        return {
            success: false,
            error: error.message,
            tasks: [],
            availableTasks: [],
        };
    }
};

/**
 * ENDPOINT 2: GET /scheduled-tasks/:taskId
 * 
 * Fetch detailed information for a specific scheduled task
 * 
 * REQUEST:
 * - Method: GET
 * - Headers: { Authorization: "Bearer <token>" }
 * - URL Params: taskId (string)
 * 
 * RESPONSE SUCCESS (200):
 * {
 *   success: true,
 *   data: {
 *     taskId: "task_123",
 *     serviceRequestId: "req_456",
 *     customer: {
 *       customerId: "cust_789",
 *       name: "John Doe",
 *       phone: "+92 300 1234567",
 *       email: "john@example.com",
 *       avatar: "https://...",
 *       rating: 4.5,
 *       completedJobs: 25
 *     },
 *     service: {
 *       title: "Plumbing Repair",
 *       category: "Plumbing",
 *       subcategory: "Leak Repair",
 *       description: "Fix kitchen sink leak. The leak started 2 days ago...",
 *       notes: "Bring necessary tools and replacement parts",
 *       includedItems: ["Inspection", "Repair", "Testing"]
 *     },
 *     location: {
 *       address: "123 Main St, Lahore",
 *       fullAddress: "House #123, Street 5, Gulberg III, Lahore, Pakistan",
 *       coordinates: [74.3140, 31.4782], // [lng, lat]
 *       landmark: "Near City Mall"
 *     },
 *     pricing: {
 *       basePrice: 2500,
 *       finalPrice: 2500,
 *       laborCost: 2000,
 *       materialCost: 500,
 *       tax: 0,
 *       currency: "PKR"
 *     },
 *     schedule: {
 *       date: "2024-11-14",
 *       time: "09:00 AM",
 *       estimatedDuration: "2-3 hours",
 *       scheduledAt: "2024-11-13T10:00:00Z"
 *     },
 *     status: "scheduled",
 *     workStatus: "pending", // pending, started, in_progress, completed
 *     offer: {
 *       offerId: "offer_123",
 *       handymanPrice: 2500,
 *       counterPrice: null,
 *       status: "accepted",
 *       acceptedAt: "2024-11-13T10:00:00Z"
 *     },
 *     handyman: {
 *       handymanId: "handy_456",
 *       name: "Your Name",
 *       phone: "+92 300 9876543",
 *       rating: 4.8,
 *       completedJobs: 127
 *     },
 *     timeline: [
 *       {
 *         status: "requested",
 *         timestamp: "2024-11-13T09:00:00Z",
 *         description: "Service requested by customer"
 *       },
 *       {
 *         status: "offered",
 *         timestamp: "2024-11-13T09:30:00Z",
 *         description: "Offer sent to customer"
 *       },
 *       {
 *         status: "accepted",
 *         timestamp: "2024-11-13T10:00:00Z",
 *         description: "Offer accepted by customer"
 *       }
 *     ],
 *     createdAt: "2024-11-13T09:00:00Z",
 *     updatedAt: "2024-11-13T10:00:00Z"
 *   }
 * }
 * 
 * RESPONSE ERROR (404):
 * {
 *   success: false,
 *   message: "Task not found"
 * }
 */
export const getTaskDetails = async (taskId) => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error("Authentication token not found");
        }

        const response = await fetch(`${API_BASE_URL}/handyman/tasks/${taskId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || "Failed to fetch task details");
        }

        return {
            success: true,
            task: data.data,
        };
    } catch (error) {
        console.error("Error fetching task details:", error);
        return {
            success: false,
            error: error.message,
            task: null,
        };
    }
};

/**
 * ENDPOINT 3: POST /scheduled-tasks/accept
 * 
 * Accept a scheduled task offer
 * 
 * REQUEST:
 * - Method: POST
 * - Headers: { Authorization: "Bearer <token>", Content-Type: "application/json" }
 * - Body: {
 *     taskId: "task_123",
 *     offerId: "offer_123",
 *     acceptedPrice: 2500,
 *     estimatedArrival: "2024-11-14T09:00:00Z" // optional
 *   }
 * 
 * RESPONSE SUCCESS (200):
 * {
 *   success: true,
 *   message: "Task accepted successfully",
 *   data: {
 *     taskId: "task_123",
 *     status: "accepted",
 *     scheduledDate: "2024-11-14",
 *     scheduledTime: "09:00 AM"
 *   }
 * }
 * 
 * RESPONSE ERROR (400):
 * {
 *   success: false,
 *   message: "Task already accepted or expired"
 * }
 * 
 * SOCKET EVENT EMITTED:
 * - Event: "task:accepted"
 * - To: Customer room
 * - Payload: { taskId, handymanId, acceptedAt }
 */
export const acceptTask = async (taskId, offerId, acceptedPrice) => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error("Authentication token not found");
        }

        const response = await fetch(`${API_BASE_URL}/handyman/scheduled-tasks/accept`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                taskId,
                offerId,
                acceptedPrice,
                estimatedArrival: null, // Can be added if needed
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || "Failed to accept task");
        }

        return {
            success: true,
            message: data.message || "Task accepted successfully",
            data: data.data,
        };
    } catch (error) {
        console.error("Error accepting task:", error);
        return {
            success: false,
            error: error.message,
        };
    }
};

/**
 * ENDPOINT 4: POST /scheduled-tasks/counter-offer
 * 
 * Make a counter offer for a scheduled task
 * 
 * REQUEST:
 * - Method: POST
 * - Headers: { Authorization: "Bearer <token>", Content-Type: "application/json" }
 * - Body: {
 *     taskId: "task_123",
 *     offerId: "offer_123",
 *     counterPrice: 3000,
 *     reason: "Additional materials required", // optional
 *     estimatedDuration: "3-4 hours" // optional
 *   }
 * 
 * RESPONSE SUCCESS (200):
 * {
 *   success: true,
 *   message: "Counter offer sent successfully",
 *   data: {
 *     taskId: "task_123",
 *     offerId: "offer_123",
 *     counterOfferId: "counter_456",
 *     counterPrice: 3000,
 *     status: "pending_customer_approval"
 *   }
 * }
 * 
 * RESPONSE ERROR (400):
 * {
 *   success: false,
 *   message: "Invalid counter offer amount"
 * }
 * 
 * SOCKET EVENT EMITTED:
 * - Event: "counterOffer"
 * - To: Customer room
 * - Payload: { taskId, handymanId, counterPrice, reason }
 */
export const makeCounterOffer = async (taskId, offerId, counterPrice, reason = "") => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error("Authentication token not found");
        }

        const response = await fetch(`${API_BASE_URL}/handyman/scheduled-tasks/counter-offer`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                taskId,
                offerId,
                counterPrice,
                reason,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || "Failed to send counter offer");
        }

        return {
            success: true,
            message: data.message || "Counter offer sent successfully",
            data: data.data,
        };
    } catch (error) {
        console.error("Error making counter offer:", error);
        return {
            success: false,
            error: error.message,
        };
    }
};

/**
 * ENDPOINT 5: POST /scheduled-tasks/reject
 * 
 * Reject a scheduled task offer
 * 
 * REQUEST:
 * - Method: POST
 * - Headers: { Authorization: "Bearer <token>", Content-Type: "application/json" }
 * - Body: {
 *     taskId: "task_123",
 *     offerId: "offer_123",
 *     reason: "Not available at scheduled time", // required
 *     reasonCode: "time_conflict" // optional: time_conflict, too_far, price_issue, other
 *   }
 * 
 * RESPONSE SUCCESS (200):
 * {
 *   success: true,
 *   message: "Task rejected successfully",
 *   data: {
 *     taskId: "task_123",
 *     status: "rejected",
 *     rejectedAt: "2024-11-13T10:00:00Z"
 *   }
 * }
 * 
 * RESPONSE ERROR (400):
 * {
 *   success: false,
 *   message: "Reason is required"
 * }
 * 
 * SOCKET EVENT EMITTED:
 * - Event: "task:rejected"
 * - To: Customer room
 * - Payload: { taskId, handymanId, reason, rejectedAt }
 */
export const rejectTask = async (taskId, offerId, reason, reasonCode = "other") => {
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error("Authentication token not found");
        }

        if (!reason || reason.trim() === "") {
            throw new Error("Rejection reason is required");
        }

        const response = await fetch(`${API_BASE_URL}/handyman/scheduled-tasks/reject`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                taskId,
                offerId,
                reason,
                reasonCode,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || "Failed to reject task");
        }

        return {
            success: true,
            message: data.message || "Task rejected successfully",
            data: data.data,
        };
    } catch (error) {
        console.error("Error rejecting task:", error);
        return {
            success: false,
            error: error.message,
        };
    }
};

/**
 * Handyman confirms payment received
 * @param {string} taskId 
 * @param {boolean} confirmed 
 */
export const confirmPaymentReceived = async (taskId, confirmed = true) => {
    try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/handyman/job/confirm-payment-received`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ taskId, confirmed }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to confirm payment");

        return { success: true, data: data.data };
    } catch (error) {
        console.error("Error confirming payment:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Customer approves work completion
 * @param {string} taskId 
 */
export const approveWorkCompletion = async (taskId) => {
    try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/services/customer/approve-completion`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ taskId }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to approve work");

        return { success: true, data: data.data };
    } catch (error) {
        console.error("Error approving work:", error);
        return { success: false, error: error.message };
    }
};

/**
 * Customer confirms completion and rates the handyman
 * @param {string} taskId 
 * @param {number} rating 
 * @param {string} review 
 */
export const confirmJobCompletion = async (taskId, rating, review) => {
    try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/services/customer/confirm-completion`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ taskId, rating, review }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to confirm completion");

        return { success: true, data: data.data };
    } catch (error) {
        console.error("Error confirming completion:", error);
        return { success: false, error: error.message };
    }
};

/**
 * HELPER: Transform API response to component-friendly format
 * 
 * Converts backend API response to the format expected by Schedule components
 */
// Helper to determine status based on offer
const getDisplayStatus = (apiTask, taskObj, isMyOffer) => {
    let status = taskObj.workStatus || taskObj.status || "scheduled";
    const offerStatus = apiTask.myOffer?.status;

    if (status === 'pending_offers' && isMyOffer) {
        if (offerStatus === 'accepted') return 'assigned'; // Handyman won!
        if (offerStatus === 'pending') return 'offered';
    }

    // Fallback logic for price=0
    const price = taskObj.basePrice || taskObj.price || apiTask.price || 0;
    if ((price === 0) && !isMyOffer && status === 'scheduled') {
        return 'pending_offers';
    }

    return status;
};

export const transformTaskData = (apiTask) => {
    if (!apiTask) return null;

    // Handle nested structure: { task: { _id, ... }, customer: {}, ... }
    const taskObj = apiTask.task || apiTask;
    const customerObj = apiTask.customer || (apiTask.task && apiTask.task.customer) || {}; // Sometimes customer is top level
    const serviceObj = apiTask.service || (apiTask.task && apiTask.task.service) || {};
    const locationObj = apiTask.location || (apiTask.task && apiTask.task.location) || {};

    const isMyOffer = !!apiTask.myOffer;
    const displayStatus = getDisplayStatus(apiTask, taskObj, isMyOffer);

    return {
        id: taskObj._id || taskObj.taskId || taskObj.id || taskObj.serviceRequestId,
        serviceRequestId: taskObj.serviceRequestId || taskObj.id || taskObj._id,
        date: taskObj.scheduledDate || taskObj.schedule?.date || new Date().toLocaleDateString(),
        time: taskObj.scheduledTime || taskObj.schedule?.time || new Date().toLocaleTimeString(),
        customer: {
            name: customerObj.name || "Customer",
            phone: customerObj.phone || "",
            email: customerObj.email || "",
            avatar: customerObj.avatar || "/static/media/default-avatar.png",
            rating: customerObj.rating || 0,
            location: customerObj.location || {},
            id: customerObj._id || customerObj.customerId
        },
        service: {
            title: serviceObj.title || serviceObj.main?.name || "Service Request",
            category: serviceObj.category || "General",
            subcategory: serviceObj.subcategory || serviceObj.sub?.map(s => s.name).join(", ") || "Service",
            description: serviceObj.description || serviceObj.main?.description || "",
            notes: serviceObj.notes || "",
            subServices: serviceObj.sub || [],
        },
        location: {
            address: locationObj.address || "",
            coordinates: locationObj.coordinates || [74.3140, 31.4782],
            fullAddress: locationObj.fullAddress || locationObj.address || "",
            distanceInKm: locationObj.distanceInKm || 0,
        },
        workDetails: {
            basePrice: taskObj.basePrice || taskObj.price || apiTask.price || 0,
            finalPrice: taskObj.finalPrice || taskObj.pricing?.finalPrice || 0,
            estimatedTime: taskObj.schedule?.estimatedDuration || "2-3 hours",
            status: displayStatus,
            scheduledDate: taskObj.scheduledDate || null,
            scheduledTime: taskObj.scheduledTime || null,
        },
        handyman: {
            name: apiTask.handyman?.name || null,
            phone: apiTask.handyman?.phone || null,
            rating: apiTask.handyman?.rating || 0,
            completedJobs: apiTask.handyman?.completedJobs || 0,
        },
        myOffer: apiTask.myOffer || null,
    };
};
